import { zodResolver } from '@hookform/resolvers/zod';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { z } from 'zod';

import { useProfileMetaData } from '@/components/Hcp/HcpCard/_hooks/useProfileMetaData';
import { Modal } from '@/components/Modal/Modal';
import { EntityType, Hcphco } from '@/core/graphql/generated/types';
import { paths } from '@/core/router/paths';
import { Permission } from '@/core/store/auth/auth.types';
import { useHasPermission } from '@/core/store/auth/authSelectors';
import { usePostDcrMutation } from '@/features/DataChangeRequest/_graphql/mutation/PostDCR.hook';
import { DcrSuccessModalContent } from '@/features/DataChangeRequest/DcrSuccessModalContent/DcrSuccessModalContent';
import { ComplianceConfirmation } from '@/features/DataChangeRequest/ReviewModalContent/ComplianceConfirmation/ComplianceConfirmation';
import { ReviewModalNotes } from '@/features/DataChangeRequest/ReviewModalContent/ReviewModalNotes/ReviewModalNotes';
import { useBreakpoint } from '@/utils/useBreakpoint';

import { reactivateDcrControlStyles as sx } from './ReactivateDcrControl.styles';

type Props = {
	profile: Hcphco | null;
	entityType: EntityType;
};

const schema = z.object({
	notes: z.string().trim().min(1, { message: 'A note is required.' }),
	isThirdPartyDataConfirmed: z.boolean().refine(val => val === true)
});
type Inputs = z.infer<typeof schema>;

export const ReactivateDcrControl = ({ profile, entityType }: Props) => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const isCollapsed = useBreakpoint('md', 'down');
	const dcrEnabled = useHasPermission(Permission.DcrEnabled);
	const [open, setOpen] = useState(false);
	const [success, setSuccess] = useState(false);
	const { getProfileName } = useProfileMetaData(profile);

	const {
		handleSubmit,
		control,
		formState: { isValid },
		reset: resetForm
	} = useForm<Inputs>({
		resolver: zodResolver(schema),
		defaultValues: {
			notes: '',
			isThirdPartyDataConfirmed: false
		},
		mode: 'onChange'
	});

	const [postDcr, { loading, reset }] = usePostDcrMutation();

	const onOpen = () => {
		setOpen(true);
	};

	const onClose = () => {
		if (loading) return;

		setOpen(false);
		setSuccess(false);
		reset();
		resetForm();
	};

	const onSubmit = async (values: Inputs) => {
		const payload =
			entityType === EntityType.Hcp
				? {
						variables: {
							payload: {
								entity: {
									hcp: {
										statusCda_VId: 'act',
										vid_V: profile?.vid_V ?? ''
									}
								},
								note: values.notes
							}
						}
					}
				: {
						variables: {
							payload: {
								entity: {
									hco: {
										statusCda_VId: 'act',
										vid_V: profile?.vid_V ?? ''
									}
								},
								note: values.notes
							}
						}
					};

		const res = await postDcr(payload);

		if (res.data?.postDcr.__typename === 'DCR') {
			setSuccess(true);
		}
	};

	const { name } = useMemo(() => getProfileName({}), [getProfileName]);

	if (!dcrEnabled) {
		return null;
	}

	return (
		<>
			{!isCollapsed ? (
				<Button
					variant='outlined'
					sx={sx.button}
					aria-label={t('Request Reactivation ')}
					onClick={onOpen}
					startIcon={<EditIcon />}
				>
					{t('Request Reactivation ')}
				</Button>
			) : (
				<IconButton
					sx={sx.button}
					aria-label={t('Request Reactivation ')}
					onClick={onOpen}
					color='primary'
				>
					<EditIcon fontSize='inherit' />
				</IconButton>
			)}

			<Modal
				open={open}
				hideCloseButton
				size='md'
				customSx={{
					body: { pb: 2 },
					footer: {
						paddingTop: 2,
						borderTop: '1px solid var(--mui-palette-divider)'
					}
				}}
				title={
					<Box sx={sx.header}>
						<Typography variant='h6' component='h2' sx={sx.title}>
							{`${t('Reactivate')} ${name}`}
						</Typography>

						{success && !pathname.includes(paths.dcrs) && (
							<Button
								sx={sx.dcrLink}
								variant='text'
								component={Link}
								to={paths.dcrs}
							>
								{t('View my DCRs')}
							</Button>
						)}
					</Box>
				}
				onClose={onClose}
				footer={
					success ? null : (
						<Box sx={sx.root}>
							<ReviewModalNotes control={control} required />
							<ComplianceConfirmation control={control} />

							<Box sx={sx.actions}>
								<Button variant='text' onClick={onClose} disabled={loading}>
									{t('Cancel')}
								</Button>

								<Button
									variant='contained'
									onClick={handleSubmit(onSubmit)}
									loading={loading}
									loadingPosition='end'
									disabled={!isValid}
								>
									{t('Submit')}
								</Button>
							</Box>
						</Box>
					)
				}
			>
				{success ? (
					<DcrSuccessModalContent onClose={onClose} />
				) : (
					<Box sx={sx.body}>
						<Typography>
							{t(
								'To help us process this request, please share a brief note on why this profile is being reactivated and any relevant details.'
							)}
						</Typography>
					</Box>
				)}
			</Modal>
		</>
	);
};
